// ═══════════════════════════════════════════════════════════════
// ProjectMetrics — Comparatif metriques par projet
// Session 32 — Analytics Metriques Projet
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { TrendingUp, FolderOpen, Zap, DollarSign, ExternalLink } from 'lucide-react';

const formatCost = (usd) => {
  if (!usd || usd === 0) return '$0.00';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
};

const ProjectMetrics = ({ onProjectSelect }) => {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const [costsRes, projectsRes] = await Promise.all([
        fetch(`/api/costs/dashboard?days=${days}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (costsRes.ok) setData(await costsRes.json());
      if (projectsRes.ok) {
        const pData = await projectsRes.json();
        setProjects(pData.projects || pData || []);
      }
    } catch (e) {
      console.error('Erreur metriques projet:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement...</div>;

  const cardStyle = {
    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
    borderRadius: '12px', padding: '20px',
  };

  // Merge cost data with project info
  const costMap = {};
  if (data?.by_project) {
    data.by_project.forEach(p => { costMap[p.project_id] = p; });
  }

  const mergedProjects = projects.map(p => ({
    ...p,
    cost_usd: costMap[p.id]?.cost_usd || 0,
    events: costMap[p.id]?.events || 0,
  })).sort((a, b) => b.cost_usd - a.cost_usd);

  const maxCost = Math.max(...mergedProjects.map(p => p.cost_usd), 0.001);
  const totalCost = mergedProjects.reduce((s, p) => s + p.cost_usd, 0);
  const totalEvents = mergedProjects.reduce((s, p) => s + p.events, 0);

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <TrendingUp size={22} style={{ color: '#3b82f6' }} />
            Metriques Projet
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Comparatif performance et couts par projet
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
              cursor: 'pointer', border: 'none', transition: 'all 0.2s',
              background: days === d ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : 'var(--input-bg)',
              color: days === d ? 'white' : 'var(--text-secondary)',
            }}>
              {d}j
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#3b82f615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FolderOpen size={18} style={{ color: '#3b82f6' }} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Projets actifs</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>{mergedProjects.length}</div>
          </div>
        </div>
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#10b98115', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DollarSign size={18} style={{ color: '#10b981' }} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Cout total ({days}j)</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>{formatCost(totalCost)}</div>
          </div>
        </div>
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#8b5cf615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={18} style={{ color: '#8b5cf6' }} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Appels LLM total</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>{totalEvents.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Projects table */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
              {['Projet', 'Cout', 'Appels LLM', 'Repartition', ''].map(h => (
                <th key={h} style={{
                  padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600',
                  color: 'var(--text-secondary)', background: 'var(--input-bg)', whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mergedProjects.map((p, i) => (
              <tr key={p.id} style={{
                borderBottom: '1px solid var(--card-border)',
                cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--input-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => onProjectSelect && onProjectSelect(p)}
              >
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontWeight: '600', color: 'var(--text)' }}>{p.name}</div>
                  {p.description && (
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.description}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px 16px', fontWeight: '600', color: '#10b981', whiteSpace: 'nowrap' }}>
                  {formatCost(p.cost_usd)}
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                  {p.events.toLocaleString()}
                </td>
                <td style={{ padding: '12px 16px', minWidth: '150px' }}>
                  <div style={{ height: '6px', background: 'var(--input-bg)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(p.cost_usd / maxCost) * 100}%`,
                      height: '100%', borderRadius: '3px',
                      background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <ExternalLink size={14} style={{ color: 'var(--text-secondary)' }} />
                </td>
              </tr>
            ))}
            {mergedProjects.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Aucun projet actif
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectMetrics;
